/**
 * Keeps an in-flight upload's File in IndexedDB so an interrupted upload
 * resumes by itself after a reload or a closed tab, the way YouTube's
 * uploader does: a File is structured-cloneable, so the browser stores its
 * bytes and hands the same File back later. Every call degrades to a no-op
 * when IndexedDB is unavailable or full (private mode, quota); the upload
 * then falls back to the manual "pick the same file" resume.
 */

const DB_NAME = "htp-scheduler-uploads";
const STORE = "files";

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function run<T>(mode: IDBTransactionMode, op: (store: IDBObjectStore) => IDBRequest<T>): Promise<T | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = op(tx.objectStore(STORE));
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
      tx.oncomplete = () => db.close();
      tx.onabort = () => {
        db.close();
        resolve(null);
      };
    } catch {
      db.close();
      resolve(null);
    }
  });
}

/** Store the file for this upload; false when the browser refused it. */
export async function saveUploadFile(assetId: string, file: File): Promise<boolean> {
  return (await run("readwrite", (s) => s.put(file, assetId))) !== null;
}

export async function loadUploadFile(assetId: string): Promise<File | null> {
  const value = await run<File>("readonly", (s) => s.get(assetId) as IDBRequest<File>);
  return value instanceof Blob ? (value as File) : null;
}

export async function deleteUploadFile(assetId: string): Promise<void> {
  await run("readwrite", (s) => s.delete(assetId));
}
